/**
 * Client-side flow evaluator for the simulator (P-1.8).
 *
 * Mirrors FLOWS.md § 8.1 evaluation semantics:
 *   1. Walk from start_node along node type-specific pointer fields
 *   2. condition evaluates JSONLogic rule
 *   3. switch evaluates expr and matches branches
 *   4. ab_test hashes session_id + test_key (deterministic)
 *   5. schedule checks current_time against window
 *   6. checkout_page applies compliance guardrails (simplified in Studio)
 *   7. capture_policy sets resolved capture_mode
 *   8. delay / fraud_check follow their semantics
 *
 * Produces the extended trace documented in API.md § Simulate trace_mode.
 */

import jsonLogic from "json-logic-js";
import type {
  ABTestNode,
  CaptureMode,
  CheckoutPageNode,
  FlowDefinition,
  FlowNode,
  JsonLogicExpression,
  SimulatorResult,
  SimulatorTraceEntry,
  SourceBinding,
} from "../../types/flow";

// json-logic-js types are overly strict; we validated the shape via our own
// TypeScript types. Cast once here.
function applyLogic(expr: JsonLogicExpression, ctx: Record<string, unknown>): unknown {
  return jsonLogic.apply(expr as unknown as Parameters<typeof jsonLogic.apply>[0], ctx);
}

// Register custom operators — FLOWS.md § 7.3
jsonLogic.add_operation("starts_with", (str: unknown, prefix: unknown) => {
  return typeof str === "string" && typeof prefix === "string" && str.startsWith(prefix);
});
jsonLogic.add_operation("ends_with", (str: unknown, suffix: unknown) => {
  return typeof str === "string" && typeof suffix === "string" && str.endsWith(suffix);
});
jsonLogic.add_operation("regex", (str: unknown, pattern: unknown) => {
  if (typeof str !== "string" || typeof pattern !== "string") return false;
  try {
    return new RegExp(pattern).test(str);
  } catch {
    return false;
  }
});
jsonLogic.add_operation("contains_any", (arr: unknown, values: unknown) => {
  if (!Array.isArray(arr) || !Array.isArray(values)) return false;
  return values.some((v) => arr.includes(v));
});

export interface EvaluatorInput {
  definition: FlowDefinition;
  context: Record<string, unknown>;
  source?: SourceBinding;
  traceMode?: boolean;
}

const MAX_DEPTH = 20;

export function evaluate(input: EvaluatorInput): SimulatorResult {
  const { definition, context, traceMode } = input;
  const path: string[] = [];
  const trace: SimulatorTraceEntry[] = [];
  let captureMode: CaptureMode = "auto";
  let capturePolicyNodeId: string | null = null;
  let finalConfig: CheckoutPageNode | null = null;
  let finalNodeId: string | null = null;

  // Enrich context with auto-populated runtime vars.
  const enrichedContext: Record<string, unknown> = {
    current_time: new Date().toISOString(),
    day_of_week: new Date().getUTCDay(),
    hour_of_day: new Date().getUTCHours(),
    session_id: `sim_${Math.random().toString(36).slice(2, 10)}`,
    ...context,
  };

  let currentId: string | null = definition.start_node;
  let depth = 0;

  while (currentId && depth < MAX_DEPTH) {
    depth++;
    const node: FlowNode | undefined = definition.nodes[currentId];
    if (!node) {
      return errorResult(path, trace, {
        code: "DANGLING_POINTER",
        message: `Pointer to unknown node "${currentId}".`,
        node_id: currentId,
      });
    }
    path.push(currentId);

    const started = performance.now();
    let output: unknown = null;
    let rulePretty: string | null = null;
    let errorString: string | null = null;
    let next: string | null = null;

    try {
      switch (node.type) {
        case "trigger":
          output = "entered";
          next = node.next || null;
          break;
        case "condition":
          rulePretty = prettyPrint(node.rule);
          output = applyLogic(node.rule, enrichedContext);
          next = output ? node.true_next : node.false_next;
          break;
        case "switch": {
          rulePretty = prettyPrint(node.expr);
          const v: unknown = applyLogic(node.expr, enrichedContext);
          output = v;
          const matched: { value: string | number | boolean; next: string } | undefined =
            node.branches.find((b) => b.value === v);
          next = matched ? matched.next : node.default_next;
          break;
        }
        case "ab_test": {
          const sid = String(enrichedContext["session_id"] ?? "");
          const hash = stableHash(sid + node.test_key);
          const totalWeight = node.variants.reduce((a, v) => a + v.weight, 0);
          if (totalWeight <= 0) throw new Error("A/B test has zero total weight");
          const pick = hash % totalWeight;
          let running = 0;
          const chosen: ABTestNode["variants"][number] = node.variants.reduce((acc, v) => {
            running += v.weight;
            return acc ?? (pick < running ? v : null);
          }, null as ABTestNode["variants"][number] | null) ?? node.variants[0];
          output = chosen.id;
          next = chosen.next;
          break;
        }
        case "schedule": {
          const now: number = Date.parse(
            String(enrichedContext["current_time"] ?? new Date().toISOString()),
          );
          const start: number = Date.parse(node.window.start);
          const end: number = Date.parse(node.window.end);
          const dow: number = new Date(now).getUTCDay();
          const withinWindow: boolean = now >= start && now <= end;
          const withinDays: boolean =
            !node.window.days_of_week || node.window.days_of_week.includes(dow);
          const active: boolean = withinWindow && withinDays;
          output = active ? "active" : "inactive";
          next = active ? node.active_next : node.inactive_next;
          break;
        }
        case "checkout_page": {
          finalConfig = node;
          finalNodeId = currentId;
          // Minimal Studio-mode compliance guardrail: country check if provided.
          const buyerCountry = enrichedContext["buyer_country"];
          if (
            typeof buyerCountry === "string" &&
            node.allowed_countries.length > 0 &&
            !node.allowed_countries.includes(buyerCountry)
          ) {
            return errorResult(path, trace, {
              code: "FLOW_PROJECT_MISMATCH",
              message: `Buyer country "${buyerCountry}" not in project's allowed_countries`,
              node_id: currentId,
            });
          }
          output = "checkout_page applied";
          next = node.next ?? null;
          break;
        }
        case "capture_policy":
          captureMode = node.mode;
          capturePolicyNodeId = currentId;
          output = node.mode;
          next = null;
          break;
        case "delay":
          output =
            node.duration.mode === "fixed"
              ? `would wait ${node.duration.iso8601}`
              : "would wait until (context-driven)";
          next = node.next || null;
          break;
        case "fraud_check":
          output = "medium"; // Studio stub — see FLOWS.md § 5.9 demo-mode behavior
          next = node.medium_next || null;
          break;
        case "http_request":
          // Studio doesn't actually fire the HTTP call — just routes to the
          // success path. The orchestrator backend is responsible for real
          // execution including the error fork on non-2xx / timeout / fail.
          output = `would call ${node.method} ${node.url || "(no URL)"}`;
          next = node.next || null;
          break;
      }
    } catch (err) {
      errorString = err instanceof Error ? err.message : String(err);
      const errorNext: string | undefined = (node as { error_next?: string }).error_next;
      if (errorNext) {
        next = errorNext;
      } else {
        const finished = performance.now();
        trace.push(buildTraceEntry(currentId, enrichedContext, rulePretty, output, finished - started, errorString));
        return errorResult(path, trace, {
          code: "FLOW_EVAL_ERROR",
          message: errorString,
          node_id: currentId,
        });
      }
    }

    const finished = performance.now();
    trace.push(buildTraceEntry(currentId, enrichedContext, rulePretty, output, finished - started, errorString));

    currentId = next;
  }

  if (depth >= MAX_DEPTH && currentId) {
    return errorResult(path, trace, {
      code: "MAX_DEPTH_EXCEEDED",
      message: "Flow traversal exceeded max depth of 20 nodes.",
    });
  }

  return {
    path,
    final_node_id: finalNodeId,
    final_config: finalConfig,
    capture_mode: captureMode,
    capture_policy_node_id: capturePolicyNodeId,
    ...(traceMode ? { trace } : {}),
  };
}

function buildTraceEntry(
  nodeId: string,
  context: Record<string, unknown>,
  rulePretty: string | null,
  output: unknown,
  durationMs: number,
  error: string | null,
): SimulatorTraceEntry {
  return {
    node_id: nodeId,
    entered_at: new Date().toISOString(),
    input_context_snapshot: { ...context },
    rule_pretty: rulePretty,
    output_value: output,
    duration_us: Math.round(durationMs * 1000),
    error,
  };
}

function errorResult(
  path: string[],
  trace: SimulatorTraceEntry[],
  error: { code: string; message: string; node_id?: string },
): SimulatorResult {
  return {
    path,
    final_node_id: null,
    final_config: null,
    capture_mode: "auto",
    capture_policy_node_id: null,
    trace,
    error,
  };
}

function prettyPrint(expr: unknown): string {
  try {
    return JSON.stringify(expr);
  } catch {
    return String(expr);
  }
}

function stableHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}
